"use client";

import { useState, useEffect } from "react";
import { Heart } from "lucide-react";
import { toggleWishlist, isInWishlist } from "@/lib/api/wishlist";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { createClient } from "@/lib/supabase/client";

interface WishlistButtonProps {
  productId: string;
  className?: string;
  size?: "sm" | "md" | "lg";
  variant?: "default" | "overlay";
  onAuthRequired?: () => void;
  // Optional: pass initial state to avoid duplicate API calls
  initialWishlisted?: boolean;
  initialAuthenticated?: boolean;
  // Callback when wishlist state changes (for syncing multiple buttons)
  onWishlistChange?: (isWishlisted: boolean) => void;
}

export default function WishlistButton({
  productId,
  className,
  size = "md",
  variant = "default",
  onAuthRequired,
  initialWishlisted,
  initialAuthenticated,
  onWishlistChange,
}: WishlistButtonProps) {
  // initial* props are first-paint hints only; the effect below always loads the
  // real auth + wishlist membership so a re-visited product shows the filled heart.
  const [isWishlisted, setIsWishlisted] = useState(initialWishlisted ?? false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(initialAuthenticated ?? false);
  const { toast } = useToast();

  // 같은 화면의 다른 찜 버튼(모바일 하단 바 등)이 바꾼 값을 따라간다.
  useEffect(() => {
    if (initialWishlisted !== undefined) setIsWishlisted(initialWishlisted);
  }, [initialWishlisted]);

  useEffect(() => {
    // 부모(ProductDetailClient)의 isWishlisted는 실제 멤버십을 로드하지 않고 기본값
    // false 로 시작하므로, 마운트 시 직접 조회해 하트 채움 상태를 동기화한다.
    checkAuthAndWishlist();
  }, [productId]);

  const checkAuthAndWishlist = async () => {
    const supabase = createClient();
    const { data } = await supabase.auth.getClaims();
    const user = data?.claims?.sub ? { id: data.claims.sub as string } : null;

    setIsAuthenticated(!!user);

    if (user) {
      const inWishlist = await isInWishlist(productId);
      setIsWishlisted(inWishlist);
    }
  };

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // 재진입 가드는 첫 await(getClaims) 이전에 걸어야 한다 — 그 뒤에 걸면
    // 인증 확인 왕복 동안 버튼이 살아 있어 연타가 toggle을 두 번 태운다.
    if (isLoading) return;
    setIsLoading(true);

    try {
    // 실시간 인증 확인: 부모(ProductDetailClient)의 user 상태는 getCurrentUser()가
    // 비동기로 끝난 뒤에야 채워지므로, initialAuthenticated prop / 로컬 state가 첫
    // 렌더 시점의 false 로 굳어 있을 수 있다. 캐시된 세션이라 빠른 getClaims()로
    // 클릭 시점에 다시 확인해 로그인 상태인데도 모달이 뜨는 문제를 막는다.
    const supabase = createClient();
    const { data } = await supabase.auth.getClaims();
    const authed = !!data?.claims?.sub;

    if (!authed) {
      if (onAuthRequired) {
        onAuthRequired();
      } else {
        toast({
          title: "로그인이 필요합니다",
          description: "위시리스트에 추가하려면 먼저 로그인해주세요.",
          variant: "destructive",
        });
      }
      return;
    }

    // 표시 상태(하트 채움 등)도 일관되게 유지
    if (!isAuthenticated) setIsAuthenticated(true);

      const result = await toggleWishlist(productId);

      if (result.error) {
        toast({
          title: "오류",
          description: result.error,
          variant: "destructive",
        });
        return;
      }

      setIsWishlisted(result.isInWishlist);
      onWishlistChange?.(result.isInWishlist);

      toast({
        title: result.isInWishlist
          ? "위시리스트에 추가됨"
          : "위시리스트에서 삭제됨",
        description: result.isInWishlist
          ? "상품이 위시리스트에 추가되었습니다."
          : "상품이 위시리스트에서 삭제되었습니다.",
      });
    } catch (error) {
      console.error("Failed to toggle wishlist:", error);
      toast({
        title: "오류",
        description: "위시리스트 처리 중 오류가 발생했습니다.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const sizeClasses = {
    sm: "w-8 h-8",
    md: "w-10 h-10",
    lg: "w-12 h-12",
  };

  const iconSizeClasses = {
    sm: "w-4 h-4",
    md: "w-5 h-5",
    lg: "w-6 h-6",
  };

  const variantClasses = {
    default: "bg-gray-100 hover:bg-gray-200",
    overlay: "bg-white/80 hover:bg-white backdrop-blur-sm",
  };

  return (
    <button
      onClick={handleClick}
      disabled={isLoading}
      className={cn(
        "rounded-full flex items-center justify-center transition-all duration-200 cursor-pointer",
        sizeClasses[size],
        variantClasses[variant],
        isLoading && "opacity-50 cursor-not-allowed",
        className,
      )}
      aria-label={isWishlisted ? "위시리스트에서 삭제" : "위시리스트에 추가"}
    >
      <Heart
        className={cn(
          iconSizeClasses[size],
          "transition-colors duration-200",
          isWishlisted
            ? "fill-red-500 text-red-500"
            : "text-gray-600 hover:text-red-500",
        )}
      />
    </button>
  );
}
